import React, { useCallback, useEffect, useRef, useState } from 'react';
import { func, oneOf } from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import SearchLoadingIndicator from '../../SearchPage/SearchLoadingIndicator/SearchLoadingIndicator';
import { AUTOSAVE_STATUS } from './useListingAutosave';

import css from './ListingAutosaveStatus.module.css';

export const AUTOSAVE_SUCCESS_VISIBLE_MS = 2300;
export const AUTOSAVE_SUCCESS_FADE_MS = 200;

const ListingAutosaveStatus = props => {
  const { status, onRetry } = props;
  const intl = useIntl();
  const previousStatusRef = useRef(status);
  const fadeTimerRef = useRef(null);
  const removeTimerRef = useRef(null);
  const [floatingStatus, setFloatingStatus] = useState(
    status === AUTOSAVE_STATUS.SAVING ? AUTOSAVE_STATUS.SAVING : null
  );
  const [isFading, setIsFading] = useState(false);
  const clearSuccessTimers = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (removeTimerRef.current) {
      clearTimeout(removeTimerRef.current);
      removeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;
    clearSuccessTimers();
    setIsFading(false);

    if (status === AUTOSAVE_STATUS.SAVING) {
      setFloatingStatus(AUTOSAVE_STATUS.SAVING);
    } else if (status === AUTOSAVE_STATUS.SAVED && previousStatus === AUTOSAVE_STATUS.SAVING) {
      setFloatingStatus(AUTOSAVE_STATUS.SAVED);
      fadeTimerRef.current = setTimeout(() => {
        setIsFading(true);
      }, AUTOSAVE_SUCCESS_VISIBLE_MS);
      removeTimerRef.current = setTimeout(() => {
        setFloatingStatus(null);
        removeTimerRef.current = null;
      }, AUTOSAVE_SUCCESS_VISIBLE_MS + AUTOSAVE_SUCCESS_FADE_MS);
    } else {
      setFloatingStatus(null);
    }

    return clearSuccessTimers;
  }, [clearSuccessTimers, status]);

  const messageId =
    status === AUTOSAVE_STATUS.UNSAVED
      ? 'EditListingAutosave.unsaved'
      : status === AUTOSAVE_STATUS.ERROR
      ? 'EditListingAutosave.failed'
      : null;
  const showInlineStatus = !!messageId;
  const isSaving = floatingStatus === AUTOSAVE_STATUS.SAVING;
  const isSaved = floatingStatus === AUTOSAVE_STATUS.SAVED;
  const savingMessage = intl.formatMessage({ id: 'EditListingAutosave.savingListing' });

  return (
    <>
      <div
        className={classNames(css.status, {
          [css.statusPlaceholder]: !showInlineStatus,
          [css.error]: status === AUTOSAVE_STATUS.ERROR,
        })}
        role={showInlineStatus ? 'status' : undefined}
        aria-live={showInlineStatus ? 'polite' : undefined}
        aria-hidden={showInlineStatus ? undefined : true}
      >
        {showInlineStatus ? <span>{intl.formatMessage({ id: messageId })}</span> : null}
        {status === AUTOSAVE_STATUS.ERROR ? (
          <button type="button" className={css.retry} onClick={onRetry}>
            {intl.formatMessage({ id: 'EditListingAutosave.retry' })}
          </button>
        ) : null}
      </div>

      {isSaving || isSaved ? (
        <div
          className={classNames(css.floatingStatus, {
            [css.success]: isSaved,
            [css.fading]: isFading,
          })}
          role={isSaved ? 'status' : undefined}
          aria-live={isSaved ? 'polite' : undefined}
        >
          {isSaving ? (
            <>
              <SearchLoadingIndicator compact messageId="EditListingAutosave.savingListing" />
              <span className={css.floatingMessage} aria-hidden="true">
                {savingMessage}
              </span>
            </>
          ) : (
            <>
              <span className={css.successIcon} aria-hidden="true">
                ✓
              </span>
              <span className={css.floatingMessage}>
                {intl.formatMessage({ id: 'EditListingAutosave.savedSuccessfully' })}
              </span>
            </>
          )}
        </div>
      ) : null}
    </>
  );
};

ListingAutosaveStatus.propTypes = {
  status: oneOf(Object.values(AUTOSAVE_STATUS)).isRequired,
  onRetry: func.isRequired,
};

export default ListingAutosaveStatus;
